// src/utils/tactic/mapTerrain.js
// ─────────────────────────────────────────────────────────────────────────
// The map's fixed geometry in LOGICAL map space (see homography.js): the
// towers, the cores, the objectives, and the walls and brush the raycaster
// treats as sight-blocking.
//
// EVERY POSITION HERE IS MEASURED, NOT GUESSED
// Antaris Battlefield is symmetric twice over, and this file leans on the 180°
// rotation throughout: one half is authored and the other is derived, so the
// two sides cannot drift apart and quietly bias one team's vision.
//
// The measurements come from a coach marking every structure on the map render.
// Each pair was then checked against that symmetry — a real pair has logical
// coordinates summing to (1,1) — which is also what DEFAULT_CORNERS was fitted
// from. Residuals run 0.005–0.015, about a dot's width on a 1920px map.
//
// The one thing NOT measured is the terrain, which is why it ships empty. See
// the note above DEFAULT_TERRAIN.
// ─────────────────────────────────────────────────────────────────────────

// ── Walls and brush ───────────────────────────────────────────────────────
//
// SHIPPED EMPTY, ON PURPOSE.
//
// There used to be ~25 wall and ~18 brush polygons here, traced by eye. Drawn
// back over the map render they turned out to sit on roads, on open grass and
// in the middle of the river — the long ridges flanking mid crossed the whole
// map through clear ground. None of it matched a real rock or bush.
//
// That mattered more than it looked, because this is what the raycaster treats
// as sight-blocking: the fog was being computed from walls that do not exist.
// Wrong terrain is worse than no terrain, so it is gone rather than hidden.
//
// Vision is therefore pure line of sight to the sight radius, stopping only at
// the map edge. Everything needed to bring terrain back is still here — the
// raycaster, the storage, the import path, and the shape of DEFAULT_TERRAIN.
// What is missing is an accurate trace.

/** Field boundary, so vision rays stop at the map edge instead of leaking. */
export const FIELD_BOUNDARY = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
];

export const DEFAULT_TERRAIN = { walls: [], brush: [] };

// ── Landmarks: read-only reference dots so the board stays orientable ─────

// Blue's nine defensive structures, ordered outward from its own base. Read off
// a coach's own annotation of the map render — the previous six were a guess at
// the layout and sat on open grass. Red's row is the 180° rotation, so the two
// sides cannot drift apart.
const BLUE_TOWERS = [
  [0.1892, 0.1497],
  [0.1602, 0.2718],
  [0.0306, 0.3323],
  [0.3903, 0.1308],
  [0.2895, 0.3462],
  [0.066, 0.4742],
  [0.3872, 0.4401],
  [0.6199, 0.1135],
  [0.1055, 0.7005],
];

// Which lane a structure guards, from how far it sits off the mid diagonal.
// Derived rather than declared, so the label can never contradict the position.
const laneOf = ([x, y]) => {
  const offMid = y - x;
  if (offMid > 0.15) return "top";
  if (offMid < -0.15) return "bot";
  return "mid";
};

const towerRow = (side) => {
  const perLane = { top: 0, bot: 0, mid: 0 };
  return BLUE_TOWERS.map((pos, i) => {
    const lane = laneOf(pos);
    perLane[lane] += 1;
    return {
      pos: side === "red" ? [1 - pos[0], 1 - pos[1]] : pos,
      lane,
      tier: perLane[lane],
      side,
      id: `${side}-${i + 1}`,
    };
  });
};

export const TOWERS = [...towerRow("blue"), ...towerRow("red")];

/**
 * The tower a given tower becomes when the board is mirrored.
 *
 * The red row is the blue row rotated 180° about the centre, so a tower's
 * mirror is the same lane and tier on the other side — only the side prefix
 * moves. That keeps "which towers are down" meaningful across a side swap.
 */
export function mirrorTowerId(id) {
  if (typeof id !== "string") return id;
  if (id.startsWith("blue-")) return `red-${id.slice(5)}`;
  if (id.startsWith("red-")) return `blue-${id.slice(4)}`;
  return id;
}

const TOWER_IDS = new Set(TOWERS.map((t) => t.id));

export const isTowerId = (id) => TOWER_IDS.has(id);

// ── Objectives ────────────────────────────────────────────────────────────
//
// Authored as ANCHORS on one half, each naming the twin it becomes under the
// 180° rotation. Same reason as the terrain: the halves must not drift apart,
// and moving one spot moves both so the map stays fair.
//
// Each side holds one blue buff and one red buff, so four camps in all.
//
// WHERE THE NUMBERS COME FROM
// A coach marked every camp and pit on the map render; those marks were read
// back through this projector. Each anchor is the average of its own mark and
// the mirror of its twin's, which splits the residual across the pair — after
// the corner refit that residual is 0.005–0.015, small enough that both halves
// of a pair land on their camp.
//
// The board still ships a landmark editor ("ปรับจุดสำคัญ") — drag a marker onto
// its real spot and it is saved and travels with an exported bundle.

const OBJECTIVE_ANCHORS = [
  {
    id: "slayer",
    short: "GOD SLAYER",
    label: "God Slayer",
    color: "#a78bfa",
    // Purple crystal pit at map.png (842, 277); Dragon basin at (1230, 653).
    pos: [0.4059, 0.6938],
    twin: { id: "dragon", short: "ABYSSAL DRAGON", label: "Abyssal Dragon", color: "#f97316" },
  },
  {
    id: "buff-blue-sage",
    short: "บัพฟ้า",
    label: "บัพฟ้า · ฝั่งบลู",
    color: "#38bdf8",
    // Marked by the coach at map.png (735, 432), paired with (1285, 465).
    pos: [0.3043, 0.53],
    twin: { id: "buff-red-sage", label: "บัพฟ้า · ฝั่งเรด" },
  },
  {
    id: "buff-blue-might",
    short: "บัพแดง",
    label: "บัพแดง · ฝั่งบลู",
    color: "#fb7185",
    // Marked by the coach at map.png (1070, 710), paired with (965, 250).
    pos: [0.4828, 0.2745],
    twin: { id: "buff-red-might", label: "บัพแดง · ฝั่งเรด" },
  },
];

export const OBJECTIVE_ANCHOR_IDS = OBJECTIVE_ANCHORS.map((a) => a.id);

export const DEFAULT_LANDMARKS = Object.fromEntries(
  OBJECTIVE_ANCHORS.map((a) => [a.id, a.pos])
);

/**
 * The full objective list — every anchor plus its 180° twin.
 *
 * Both carry `anchorId`, so dragging either one drives the same stored
 * position and the pair can never come apart.
 */
export function buildObjectives(landmarks) {
  const out = [];
  for (const a of OBJECTIVE_ANCHORS) {
    const stored = landmarks?.[a.id];
    const pos = Array.isArray(stored) && stored.length === 2 ? stored : a.pos;

    out.push({
      id: a.id,
      short: a.short,
      label: a.label,
      color: a.color,
      pos,
      anchorId: a.id,
      mirrored: false,
    });
    out.push({
      id: a.twin.id,
      short: a.twin.short || a.short,
      label: a.twin.label,
      color: a.twin.color || a.color,
      pos: [1 - pos[0], 1 - pos[1]],
      anchorId: a.id,
      mirrored: true,
    });
  }
  return out;
}

/** Objectives at their shipped positions. */
export const OBJECTIVES = buildObjectives();

/**
 * The two core structures — "ป้อมใหญ่". Marked by the coach at map.png
 * (400, 857) and (1440, 165); the pair mirrors to within 0.010, the same margin
 * as every other measured pair.
 *
 * They are towers in the sense that matters here — they watch their own base —
 * but they are not in TOWERS, because "this plan assumes their core is down"
 * is not a thing a coach draws.
 */
export const BASES = [
  { side: "blue", id: "blue-core", pos: [0.0533, 0.1945] },
  { side: "red", id: "red-core", pos: [0.9467, 0.8055] },
];

// ── Geometry helpers shared with the visibility engine ────────────────────

/** Flatten polygons into [ax, ay, bx, by] edge segments for raycasting. */
export function polygonsToSegments(polygons = []) {
  const segs = [];
  for (const poly of polygons) {
    if (!poly || poly.length < 2) continue;
    for (let i = 0; i < poly.length; i++) {
      const [ax, ay] = poly[i];
      const [bx, by] = poly[(i + 1) % poly.length];
      segs.push([ax, ay, bx, by]);
    }
  }
  return segs;
}

/** Standard crossing-number test. */
export function pointInPolygon(x, y, poly = []) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const straddles = (yi > y) !== (yj > y);
    const crossesLeft = x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi;
    if (straddles && crossesLeft) inside = !inside;
  }
  return inside;
}

/** Index of the first polygon containing the point, or -1. */
export function findContainingPolygon(x, y, polygons = []) {
  for (let i = 0; i < polygons.length; i++) {
    if (pointInPolygon(x, y, polygons[i])) return i;
  }
  return -1;
}

/** Blocking segments = terrain walls plus the field boundary. */
export function buildBlockingSegments(terrain = DEFAULT_TERRAIN) {
  return [
    ...polygonsToSegments(terrain.walls || []),
    ...polygonsToSegments([FIELD_BOUNDARY]),
  ];
}
